import { Injectable } from '@angular/core';
import { ChartConfiguration, ChartOptions } from 'chart.js';

@Injectable({
  providedIn: 'root'
})
export class DashboardChartService {

  public lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: { tension: 0, borderWidth: 2 },
      point: { radius: 2, hitRadius: 10, hoverRadius: 6, backgroundColor: '#0A0A0A' }
    },
    scales: {
      y: { grid: { color: '#222222' }, border: { dash: [2, 4] }, min: 0, max: 90, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: {
      legend: { display: false }
    }
  };

  public lineChartOptionsWithLegend: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: { tension: 0, borderWidth: 2 },
      point: { radius: 2, hitRadius: 10, hoverRadius: 6, backgroundColor: '#0A0A0A' }
    },
    scales: {
      y: { grid: { color: '#222222' }, border: { dash: [2, 4] }, min: 0, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: {
      legend: { display: true, labels: { color: '#A1A1AA', font: { family: '"JetBrains Mono", monospace', size: 11 } } }
    }
  };

  public bubbleChartOptions: ChartOptions<'bubble'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { title: { display: true, text: 'FPS Promedio', color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } }, grid: { color: '#222222' }, min: 0, max: 90, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { title: { display: true, text: 'Consumo Máximo RAM (MB)', color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } }, grid: { color: '#222222' }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: {
      legend: { display: true, labels: { color: '#A1A1AA', font: { family: '"JetBrains Mono", monospace', size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `RAM: ${ctx.parsed.x}MB | FPS: ${ctx.parsed.y} | Crashes: ${ctx.raw._crashes}`
        }
      }
    }
  };

  public barChartOptionsWithLegend: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { grid: { color: '#222222' }, border: { dash: [2, 4] }, min: 0, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: {
      legend: { display: true, labels: { color: '#A1A1AA', font: { family: '"JetBrains Mono", monospace', size: 11 } } }
    }
  };

  public doughnutChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#A1A1AA', font: { family: '"JetBrains Mono", monospace', size: 11 } } }
    },
    cutout: '85%',
    elements: {
      arc: { borderColor: '#111111', borderWidth: 2 } // Match new card background color
    }
  };

  public mixedChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { type: 'linear', display: true, position: 'left', grid: { color: '#222222' }, border: { dash: [2, 4] }, min: 0, ticks: { color: '#71717A', stepSize: 1, font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, min: 0, max: 90, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: { legend: { display: true, labels: { color: '#A1A1AA', font: { family: '"JetBrains Mono", monospace', size: 11 } } } }
  };

  public stabilityChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Estabilidad (%)', color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } }, grid: { color: '#222222' }, border: { dash: [2, 4] }, min: 0, max: 100, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: { 
      legend: { display: true, labels: { color: '#A1A1AA', font: { family: '"JetBrains Mono", monospace', size: 11 } } },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (ctx: any) => `Estabilidad: ${ctx.raw}%`
        }
      }
    }
  };

  public apdexChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: { line: { tension: 0, borderWidth: 2 }, point: { radius: 2, hitRadius: 10, hoverRadius: 6, backgroundColor: '#0A0A0A' } },
    scales: {
      y: { grid: { color: '#222222' }, border: { dash: [2, 4] }, min: 0, max: 1, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#71717A', font: { family: '"JetBrains Mono", monospace', size: 10 } } }
    },
    plugins: { legend: { display: false } }
  };

  getAvgFps(session: any): number {
    if (!session.performanceCount || session.performanceCount === 0) return session.avgFps || 0;
    return (session.sumFps || 0) / session.performanceCount;
  }

  processFpsChartData(sortedSessions: any[], labels: string[]): ChartConfiguration<'line'>['data'] {
    const fpsData = sortedSessions.map(s => this.getAvgFps(s));
    return {
      labels: labels,
      datasets: [{
        data: fpsData,
        borderColor: '#06B6D4', // Cyan
        backgroundColor: 'transparent',
        fill: false,
        pointBackgroundColor: '#0A0A0A',
        pointBorderColor: '#06B6D4'
      }]
    };
  }

  processCpuGpuChartData(sortedSessions: any[], labels: string[]): ChartConfiguration<'line'>['data'] {
    const cpuData = sortedSessions.map(s => {
      if (!s.cpuCount || s.cpuCount === 0) return 0;
      return (s.sumCpu || 0) / s.cpuCount;
    });
    
    const gpuData = sortedSessions.map(s => {
      if (!s.gpuCount || s.gpuCount === 0) return 0;
      return (s.sumGpu || 0) / s.gpuCount;
    });

    return {
      labels: labels,
      datasets: [
        {
          label: 'Nivel Promedio CPU',
          data: cpuData,
          borderColor: '#10B981', // Emerald
          backgroundColor: 'transparent',
          pointBackgroundColor: '#0A0A0A',
          pointBorderColor: '#10B981'
        },
        {
          label: 'Nivel Promedio GPU',
          data: gpuData,
          borderColor: '#F59E0B', // Amber
          backgroundColor: 'transparent',
          pointBackgroundColor: '#0A0A0A',
          pointBorderColor: '#F59E0B'
        }
      ]
    };
  }

  processNetworkChartData(sortedSessions: any[], labels: string[]): ChartConfiguration<'bar'>['data'] {
    const rxData = sortedSessions.map(s => {
      if (!s.networkCount || s.networkCount === 0) return 0;
      return (s.sumNetworkRx || 0) / 1048576; // MB
    });
    const txData = sortedSessions.map(s => {
      if (!s.networkCount || s.networkCount === 0) return 0;
      return (s.sumNetworkTx || 0) / 1048576; // MB
    });

    return {
      labels: labels,
      datasets: [
        {
          label: 'Rx (Recibidos MB)',
          data: rxData,
          backgroundColor: '#10B981', // Emerald
          borderWidth: 1,
          borderColor: '#059669'
        },
        {
          label: 'Tx (Enviados MB)',
          data: txData,
          backgroundColor: '#EF4444', // Red
          borderWidth: 1,
          borderColor: '#DC2626'
        }
      ]
    };
  }

  processApdexChartData(sortedSessions: any[], labels: string[]): ChartConfiguration<'line'>['data'] {
    const apdexData = sortedSessions.map(s => s.apdexScore || 0);
    return {
      labels: labels,
      datasets: [{
        data: apdexData,
        borderColor: '#06B6D4', // Cyan
        backgroundColor: 'transparent',
        fill: false,
        pointBackgroundColor: '#0A0A0A',
        pointBorderColor: '#06B6D4'
      }]
    };
  }

  processRamFpsScatterData(sortedSessions: any[]): ChartConfiguration<'scatter'>['data'] {
    const scatterData = sortedSessions.map(s => {
      const isCrash = (s.crashed || s.hasCrash || !!s.exception) ? 1 : 0;
      return {
        x: s.maxRamUsage || 0,
        y: this.getAvgFps(s),
        r: isCrash ? 18 : 8,
        _crashes: isCrash
      };
    }).filter(point => point.x > 0);

    return {
      datasets: [{
        label: 'Sesiones',
        data: scatterData as any,
        backgroundColor: 'transparent', 
        borderColor: '#06B6D4',
        borderWidth: 1,
        hoverBackgroundColor: '#06B6D4',
        hoverBorderColor: '#FFFFFF'
      }]
    };
  }

  processVersionStabilityChartData(sortedSessions: any[]): any {
    const versionsMap = new Map<string, { total: number, crashes: number }>();
    sortedSessions.forEach(s => {
      const v = s.appVersion || 'Desconocida';
      if (!versionsMap.has(v)) versionsMap.set(v, { total: 0, crashes: 0 });
      const stats = versionsMap.get(v)!;
      stats.total++;
      if (s.crashed || s.hasCrash || !!s.exception) stats.crashes++;
    });

    const versionLabels = Array.from(versionsMap.keys());
    const stabilityRates = versionLabels.map(v => {
      const stats = versionsMap.get(v)!;
      if (stats.total === 0) return 100;
      return Number(((1 - (stats.crashes / stats.total)) * 100).toFixed(1));
    });

    return {
      labels: versionLabels,
      datasets: [
        {
          type: 'bar',
          label: '% de Estabilidad',
          data: stabilityRates,
          backgroundColor: '#10B981', // Emerald
          borderWidth: 1,
          borderColor: '#059669'
        }
      ]
    };
  }

  processErrorHealthData(sortedSessions: any[]): ChartConfiguration<'doughnut'>['data'] {
    let clean = 0;
    let withErrors = 0;
    let crashed = 0;
    sortedSessions.forEach(s => {
      if (s.crashed || s.hasCrash || !!s.exception) crashed++;
      else if (s.errorCount && s.errorCount > 0) withErrors++;
      else clean++;
    });

    return {
      labels: ['Limpias', 'Con Errores', 'Crashes'],
      datasets: [{
        data: [clean, withErrors, crashed],
        backgroundColor: ['#10B981', '#F59E0B', '#EF4444'], // Emerald, Amber, Red
        borderWidth: 2,
        borderColor: '#111111',
        hoverOffset: 4
      }]
    };
  }

  processErrorTimelineData(sortedSessions: any[], labels: string[]): ChartConfiguration<'bar'>['data'] {
    const errorTimeline = sortedSessions.map(s => (s.crashed || s.hasCrash || !!s.exception) ? 1 : 0);
    return {
      labels: labels,
      datasets: [{
        label: 'Cantidad de Crashes',
        data: errorTimeline,
        backgroundColor: '#EF4444', 
        borderColor: '#DC2626',
        borderWidth: 1
      }]
    };
  }

  processErrorFpsMixedData(sortedSessions: any[], labels: string[]): any {
    const errorTimeline = sortedSessions.map(s => (s.crashed || s.hasCrash || !!s.exception) ? 1 : 0);
    const fpsData = sortedSessions.map(s => this.getAvgFps(s));
    
    return {
      labels: labels,
      datasets: [
        {
          type: 'bar',
          label: 'Crashes',
          data: errorTimeline,
          backgroundColor: '#EF4444', // Red
          borderColor: '#DC2626',
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          type: 'line',
          label: 'FPS Promedio',
          data: fpsData,
          borderColor: '#10B981', // Emerald
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointBackgroundColor: '#0A0A0A',
          pointBorderColor: '#10B981',
          yAxisID: 'y1'
        }
      ]
    };
  }
}
